import { logUsage, logGreyError } from 'owa-analytics';
import type { TableQuery } from 'owa-mail-list-store';
import { getTableViewFromTableQuery } from 'owa-mail-triage-table-utils';
import { onAddTable, onRemoveTable } from './onUpdateTables';
import { getMailboxInfoFromTableViewId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';

// This map is used to keep track of the tables that should always be in cache.
// When tables are added to this map, they are also added to the store and subscribed to.
// When tables are removed from this map, they are also removed from the store and unsubscribed from.
const currentInboxes = new Map<string, string>();

// Keep track of the folders that the table views in cache correspond to, as a
// proxy for the selectFolder code to determine if the folder contents are already
// in memory
const folderIdList: Map<string, string> = new Map<string, string>();

export function add(inboxView: string, tableQuery: TableQuery): void {
    const tableView = getTableViewFromTableQuery(tableQuery);

    currentInboxes.set(inboxView, tableView.id);
    folderIdList.set(tableView.id, tableView.tableQuery.folderId);

    // Add table to store and set up row notification subscription
    onAddTable(tableView);

    logUsage('AlwaysInCacheTableAdded', getMailboxInfoFromTableViewId(tableView.id), {
        id: tableView.id,
    });
}

export function clear(inboxView: string): void {
    const tableViewId = currentInboxes.get(inboxView);
    if (tableViewId) {
        currentInboxes.delete(inboxView);
        folderIdList.delete(tableViewId);

        // Remove table from store and unsubscribe from row notifications
        onRemoveTable(tableViewId);

        // This isn't necessarily an error, but we don't expect it to happen often
        // and we are seeing users end up without an inbox row subscription.
        // Using grey error gives us callstacks to use if we identify a suspicious event.

        logGreyError(
            'AlwaysInCacheTableRemoved',
            getMailboxInfoFromTableViewId(tableViewId),
            new Error('AlwaysInCacheTableRemoved'),
            {
                id: tableViewId,
            }
        );
    }
}

export function has(inboxView: string): boolean {
    return currentInboxes.has(inboxView);
}

// Helper function for selectFolder to determine if the folder contents are already in memory
export function alwaysInCacheTableViewCacheHasFolderId(folderId: string): boolean {
    const folderIds = [...folderIdList.values()];
    return folderIds.includes(folderId);
}
